import _ from 'lodash';

import getFormattedPricing from '../utils';

// Column key constants used to map a cell value to its column independently of
// the column's position in the authored `headerList` (Contentful). Matching is
// case-insensitive so `Grouped`/`grouped` both resolve to the group column.
// `MONTHLY_PRICE`/`UNIT_PRICE` are authored price columns (static display) and
// `NAME` is the product-name column inferred for the first keyless column.
// `RATE_PLAN_UNITS`/`MTM_UNITS`/`TOTAL_UNITS` are the hardware-style unit
// columns: an auto-derived count, a rep-editable count, and their sum.
export const COLUMN_KEYS = {
  GROUPED: 'grouped',
  RATE_PLAN: 'rateplan',
  UNITS: 'units',
  TOTAL: 'total',
  MONTHLY_PRICE: 'monthlyprice',
  UNIT_PRICE: 'unitprice',
  NAME: 'name',
  RATE_PLAN_UNITS: 'rateplanunits',
  MTM_UNITS: 'mtmunits',
  TOTAL_UNITS: 'totalunits'
};

const normaliseKey = key => (typeof key === 'string' ? key.toLowerCase() : '');

/**
 * Infer a column's role from its header `title` when no `columnKey` is authored.
 * Tables in Contentful key columns inconsistently (some omit `columnKey` on
 * `Units`/`Total` and add `monthlyPrice`/`unitPrice`); the title fallback keeps
 * the correct interactive/price cell rendering instead of defaulting every
 * keyless column to the product name (FR-002b, data-model VR-10).
 *
 * @param {string} title
 * @returns {string|null}
 */
const inferRoleFromTitle = title => {
  const normalised = normaliseKey(title).trim();
  if (!normalised) return null;
  if (/monthly\s*price/.test(normalised)) return COLUMN_KEYS.MONTHLY_PRICE;
  if (/unit\s*price/.test(normalised)) return COLUMN_KEYS.UNIT_PRICE;
  // The unit-count titles must be tested before the generic `total` match,
  // otherwise "Total Units" resolves to the price-total column.
  if (/rate\s*plan\s*units?$/.test(normalised)) {
    return COLUMN_KEYS.RATE_PLAN_UNITS;
  }
  if (/mtm\s*units?$/.test(normalised)) return COLUMN_KEYS.MTM_UNITS;
  if (/total\s*units?$/.test(normalised)) return COLUMN_KEYS.TOTAL_UNITS;
  if (/total/.test(normalised)) return COLUMN_KEYS.TOTAL;
  if (/^units?$/.test(normalised)) return COLUMN_KEYS.UNITS;
  return null;
};

/**
 * Build ordered column descriptors from a header entry's `headerList`.
 * Supports a different number of columns per table. The role is resolved from
 * `columnKey` first and from the header `title` as a fallback; the first column
 * that still has no role is treated as the product-name column (`NAME`). Any
 * remaining unresolved column keeps `key: null` and renders empty.
 *
 * @param {object} headerEntry - the `isHeader` ioTTableListItem entry
 * @returns {Array<{ key: string|null, title: string, index: number }>}
 */
export const buildColumnDescriptors = headerEntry => {
  const headerList = _.get(headerEntry, 'fields.headerList', []);
  if (!Array.isArray(headerList)) return [];

  let nameAssigned = false;

  return headerList.map((header, index) => {
    const rawKey = _.get(header, 'fields.columnKey', null);
    const title = _.get(header, 'fields.title', '');
    let key = rawKey ? normaliseKey(rawKey) : inferRoleFromTitle(title);

    if (!key && !nameAssigned) {
      key = COLUMN_KEYS.NAME;
      nameAssigned = true;
    }

    return { key, title, index };
  });
};

/**
 * Detect whether term data is a "double array" (an array whose elements are
 * themselves arrays = multiple selectable groups) versus a "single array"
 * (a flat list of term entries). Malformed/empty input returns false so the
 * caller falls back to a safe presentation.
 *
 * @param {*} terms
 * @returns {boolean}
 */
export const isDoubleArray = terms =>
  Array.isArray(terms) &&
  terms.length > 0 &&
  terms.every(element => Array.isArray(element));

/**
 * Resolve the static/derived value for a column on a data entry. Interactive
 * columns (group label, term/ratePlan, units, total) are rendered by the row
 * component; this helper covers the product-name column, the authored price
 * columns (`monthlyPrice`/`unitPrice`, shown as static formatted text), and the
 * group label. Returns a primitive (string/number) — formatting/JSX is the
 * caller's responsibility — to keep this module pure and unit-testable.
 *
 * @param {object} entry - a data ioTTableListItem entry
 * @param {{ key: string|null, title: string }} columnDescriptor
 * @returns {string|number|null}
 */
export const resolveColumnValue = (entry, columnDescriptor) => {
  const fields = _.get(entry, 'fields', {});
  const key = _.get(columnDescriptor, 'key', null);

  if (key === COLUMN_KEYS.GROUPED) {
    return _.get(fields, 'rateplanType', '');
  }

  if (key === COLUMN_KEYS.NAME) {
    return _.get(fields, 'rateplanName', '');
  }

  if (key === COLUMN_KEYS.MONTHLY_PRICE) {
    const monthlyPrice = _.get(fields, 'monthlyPrice', null);
    return monthlyPrice === null || monthlyPrice === undefined
      ? ''
      : getFormattedPricing(monthlyPrice);
  }

  if (key === COLUMN_KEYS.UNIT_PRICE) {
    const unitPrice = _.get(fields, 'unitPrice', null);
    return unitPrice === null || unitPrice === undefined
      ? ''
      : getFormattedPricing(unitPrice);
  }

  // Interactive columns are handled by the row component; any remaining
  // unresolved column renders empty rather than repeating the product name.
  return '';
};

/**
 * Compute per-row group metadata for the leading group column. Mirrors
 * `buildServiceMeta` but groups by `rateplanType`. A row contributes to a
 * merged group cell only when it has `hasStartGroupColumn === true`; header
 * rows reset the running group.
 *
 * @param {Array<object>} tableList
 * @returns {Array<{ shouldRenderGroupCell: boolean, rowSpan: number }>}
 */
export const buildGroupMeta = tableList => {
  const list = Array.isArray(tableList) ? tableList : [];
  const meta = list.map(() => ({ shouldRenderGroupCell: false, rowSpan: 1 }));

  let groupStart = -1;
  let groupType = null;

  list.forEach((entry, index) => {
    if (_.get(entry, 'fields.isHeader', false)) {
      groupStart = -1;
      groupType = null;
      return;
    }

    if (!_.get(entry, 'fields.hasStartGroupColumn', false)) {
      groupStart = -1;
      groupType = null;
      return;
    }

    const type = _.get(entry, 'fields.rateplanType', '');

    if (type && type === groupType && groupStart !== -1) {
      meta[groupStart].rowSpan += 1;
    } else {
      meta[index] = { shouldRenderGroupCell: true, rowSpan: 1 };
      groupStart = index;
      groupType = type;
    }
  });

  return meta;
};

/**
 * Resolve the unit price for a selected term from a (single or double) terms
 * structure.
 */
export const getSelectedTermPrice = (terms, selectedTermName) => {
  const flat = isDoubleArray(terms) ? _.flatten(terms) : terms;
  if (!Array.isArray(flat)) return 0;
  const match = flat.find(
    term => _.get(term, 'fields.termName') === selectedTermName
  );
  return _.get(match, 'fields.termPrice', 0);
};

/**
 * Compute the monthly and one-time pricing contribution for a row.
 *
 * - Hardware-style rows (`mtmUnits` supplied): `unitPrice × mtmUnits` is a
 *   one-time charge, suppressed entirely on a renewal order (FR-022). The
 *   caller passes the already-clamped displayed total (floor + surplus), so the
 *   device is charged once rather than once per contributing source (FR-022a).
 * - With a selected term: termPrice × quantity is added to the monthly total
 *   for every term, including month-to-month (mirrors the rate plan tables).
 *   The device a month-to-month plan implies is charged one-time through the
 *   hardware row (`mtmUnits`), not here.
 * - Without a term: `monthlyPrice × Units` is added to monthly when
 *   `isPriceAddedToMonthly`, and `unitPrice × Units` is added to one-time when
 *   `isPriceAddedToOneTime` (both may apply). Totals scale with the entered
 *   quantity, so a row with zero Units contributes nothing (FR-011a).
 *
 * @param {object} entry
 * @param {{ term?: string, quantity?: number, mtmUnits?: number, isRenewal?: boolean }} [selection]
 * @returns {{ monthly: number, oneTime: number }}
 */
export const computeRowPricing = (entry, selection = {}) => {
  const fields = _.get(entry, 'fields', {});
  const term = _.get(selection, 'term', '');
  const quantity = Number(_.get(selection, 'quantity', 0)) || 0;
  const mtmUnits = _.get(selection, 'mtmUnits', null);

  // Renewal suppression is scoped to hardware rows only: storage and accessory
  // rows price through the `quantity` path below and still charge on renewals.
  if (mtmUnits !== null && mtmUnits !== undefined) {
    if (_.get(selection, 'isRenewal', false)) return { monthly: 0, oneTime: 0 };
    const unitPrice = Number(_.get(fields, 'unitPrice', 0)) || 0;
    return { monthly: 0, oneTime: unitPrice * (Number(mtmUnits) || 0) };
  }

  if (term) {
    const unitPrice = getSelectedTermPrice(_.get(fields, 'terms', []), term);
    return { monthly: unitPrice * quantity, oneTime: 0 };
  }

  let monthly = 0;
  let oneTime = 0;
  if (_.get(fields, 'isPriceAddedToMonthly', false)) {
    monthly += (Number(_.get(fields, 'monthlyPrice', 0)) || 0) * quantity;
  }
  if (_.get(fields, 'isPriceAddedToOneTime', false)) {
    oneTime += (Number(_.get(fields, 'unitPrice', 0)) || 0) * quantity;
  }
  return { monthly, oneTime };
};

/**
 * Determine whether an interactive row has all of its required (rendered
 * selectable/input) fields filled so it can be promoted to a completed product.
 *
 * The requirement set is data-driven per table:
 * - A term is required only when the row renders a term dropdown (a double
 *   array of terms on a normal rate-plan column). A single-array term
 *   auto-selects, so it is not treated as an outstanding requirement.
 * - A quantity is required only when the row renders a quantity input (a
 *   `Units` column on a row that is priced or term-bearing).
 *
 * A row that renders no selectable/input field is never "completable" as a
 * product and returns false (VR-8, data-model §3.6a).
 *
 * @param {object} entry
 * @param {{ term?: string, quantity?: number }} [selection]
 * @param {Array<{ key: string|null }>} [columns]
 * @returns {boolean}
 */
export const isRowComplete = (entry, selection = {}, columns = []) => {
  const fields = _.get(entry, 'fields', {});
  const terms = _.get(fields, 'terms', []);
  const hasTermData = Array.isArray(terms) && terms.length > 0;
  const isPriced = Boolean(
    fields.isPriceAddedToMonthly || fields.isPriceAddedToOneTime
  );
  const columnList = Array.isArray(columns) ? columns : [];
  const hasRatePlanColumn = columnList.some(
    column => _.get(column, 'key') === COLUMN_KEYS.RATE_PLAN
  );
  const hasUnitsColumn = columnList.some(
    column => _.get(column, 'key') === COLUMN_KEYS.UNITS
  );
  const hasMtmUnitsColumn = columnList.some(
    column => _.get(column, 'key') === COLUMN_KEYS.MTM_UNITS
  );

  // A hardware-style row is complete once it has any units at all — whether the
  // rep entered them or a month-to-month rate plan auto-populated them.
  if (hasMtmUnitsColumn) {
    return Number(_.get(selection, 'mtmUnits', 0)) > 0;
  }

  // A dropdown is shown (and a term selection is therefore required) when the
  // row has more than one term option — whether the terms are a flat list or a
  // flattened double array. A single option auto-selects (label) and is not an
  // outstanding requirement.
  const termOptions = isDoubleArray(terms) ? _.flatten(terms) : terms;
  const termOptionCount = Array.isArray(termOptions) ? termOptions.length : 0;

  const requiresTerm =
    hasRatePlanColumn &&
    hasTermData &&
    termOptionCount > 1 &&
    !fields.isThisRatePlan;
  const requiresQuantity = hasUnitsColumn && (hasTermData || isPriced);

  if (!requiresTerm && !requiresQuantity) return false;
  if (requiresTerm && !_.get(selection, 'term')) return false;
  if (requiresQuantity && !(Number(_.get(selection, 'quantity', 0)) > 0)) {
    return false;
  }
  return true;
};

export { getFormattedPricing };
